import { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, FileCode } from 'lucide-react';
import { apiService } from '@/shared/services/api';
import StatusBadge from '@/shared/components/StatusBadge';
import RejectSubmissionModal from './RejectSubmissionModal';
import { useAdmin } from '@/admin/hooks/useAdmin';
import type { Submission } from '@/submissions/types';

interface ExerciseSubmissionsModalProps {
  isOpen: boolean;
  userId: string;
  guideNumber: number;
  exerciseNumber: number;
  /** Cuatrimestre con el que se acota la lista de envíos (D26). */
  term?: string;
  onClose: () => void;
  onRejected?: () => void;
}

export default function ExerciseSubmissionsModal({
  isOpen,
  userId,
  guideNumber,
  exerciseNumber,
  term,
  onClose,
  onRejected,
}: ExerciseSubmissionsModalProps) {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  const { isAdmin } = useAdmin();

  const fetchSubmissions = (selectCreatedAt?: string) => {
    setIsLoading(true);
    setError(null);
    apiService
      .getAdminUserSubmissions(userId, guideNumber, exerciseNumber, term)
      .then((response) => {
        if (response.success && response.data) {
          setSubmissions(response.data);
          if (selectCreatedAt) {
            const updated = response.data.find((s) => s.createdAt === selectCreatedAt);
            setSelectedSubmission(updated ?? response.data[0] ?? null);
          } else {
            setSelectedSubmission(response.data[0] ?? null);
          }
        } else {
          setError('No se pudieron cargar los envíos.');
        }
      })
      .catch(() => {
        setError('Error de conexión. Intentá de nuevo.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!isOpen) return;
    setSubmissions([]);
    setSelectedSubmission(null);
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId, guideNumber, exerciseNumber, term]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };

  const handleReject = async (rejectedReason: string, notifyUser: boolean) => {
    if (!selectedSubmission) return;
    await apiService.rejectSubmission({
      userId: selectedSubmission.userId,
      guideNumber: selectedSubmission.guideNumber,
      exerciseNumber: selectedSubmission.exerciseNumber,
      createdAt: selectedSubmission.createdAt,
      rejectedReason,
      notifyUser,
    });
    setRejectModalOpen(false);
    fetchSubmissions(selectedSubmission.createdAt);
    onRejected?.();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-200 ${
            isClosing ? 'opacity-0' : 'opacity-50'
          }`}
          onClick={handleClose}
        />

        {/* Modal */}
        <div
          className={`relative bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col transform transition-all duration-200 ${
            isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">
              Envíos — Guía {guideNumber} / Ejercicio {exerciseNumber}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden flex min-h-0">
            {isLoading && (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            )}

            {error && !isLoading && (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 max-w-md">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            )}

            {!isLoading && !error && submissions.length === 0 && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <FileCode className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Sin envíos para este ejercicio</p>
                </div>
              </div>
            )}

            {!isLoading && !error && submissions.length > 0 && (
              <>
                {/* Panel izquierdo — lista de submissions */}
                <div className="w-64 flex-shrink-0 border-r border-gray-200 overflow-y-auto">
                  {submissions.map((sub) => {
                    const isSelected = selectedSubmission === sub;
                    const date = new Date(sub.createdAt).toLocaleString('es-AR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                    return (
                      <button
                        key={sub.createdAt}
                        onClick={() => setSelectedSubmission(sub)}
                        className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${
                          isSelected
                            ? 'bg-primary-50 border-l-2 border-l-primary-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <p className="text-xs text-gray-600 mb-1">{date}</p>
                        <div className="flex items-center gap-2">
                          <StatusBadge success={sub.success} rejected={!!sub.rejectedBy} />
                          {sub.executionTime && (
                            <span className="text-xs text-gray-400">{sub.executionTime}</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Panel derecho — detalle */}
                <div className="flex-1 overflow-y-auto p-6">
                  {selectedSubmission ? (
                    <>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guía</p>
                          <p className="text-sm font-medium text-gray-900">{selectedSubmission.guideNumber}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Ejercicio</p>
                          <p className="text-sm font-medium text-gray-900">{selectedSubmission.exerciseNumber}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Fecha</p>
                          <p className="text-sm font-medium text-gray-900">
                            {new Date(selectedSubmission.createdAt).toLocaleString('es-AR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                            })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Estado</p>
                          <StatusBadge success={selectedSubmission.success} rejected={!!selectedSubmission.rejectedBy} />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Tiempo de ejecución</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedSubmission.executionTime ?? '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Memoria utilizada</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedSubmission.memoryUsage ?? '—'}
                          </p>
                        </div>
                      </div>

                      {/* Info de rechazo */}
                      {selectedSubmission.rejectedBy && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-700 font-semibold uppercase tracking-wide mb-1">
                            Motivo de rechazo
                          </p>
                          <p className="text-sm text-amber-800 mb-1">{selectedSubmission.rejectedReason}</p>
                          <p className="text-xs text-amber-600">
                            Rechazado por{' '}
                            {selectedSubmission.rejectedBy.fullName ?? selectedSubmission.rejectedBy.email}
                          </p>
                        </div>
                      )}

                      {/* Botón invalidar */}
                      {isAdmin && selectedSubmission.success && !selectedSubmission.rejectedBy && (
                        <div className="mb-4">
                          <button
                            onClick={() => setRejectModalOpen(true)}
                            className="px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-300 rounded-lg transition-colors"
                          >
                            Rechazar envío
                          </button>
                        </div>
                      )}

                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Código enviado</p>
                        <pre className="p-4 bg-gray-100 rounded-lg text-sm font-mono overflow-x-auto whitespace-pre-wrap break-all">
                          {selectedSubmission.code}
                        </pre>
                      </div>
                    </>
                  ) : null}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end px-6 py-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={handleClose}
              className="px-5 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      <RejectSubmissionModal
        isOpen={rejectModalOpen}
        guideNumber={guideNumber}
        exerciseNumber={exerciseNumber}
        onConfirm={handleReject}
        onClose={() => setRejectModalOpen(false)}
      />
    </>
  );
}
